import { getStateCallbacks, Room } from "@colyseus/sdk"
import firebase from "firebase/compat/app"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { FAIRY_WANDS_BY_SYNERGY_LEVEL, SynergyTriggers } from "../../../config"
import { FlowerMonByPot, FlowerPots } from "../../../core/flower-pots"
import { DishByPkm } from "../../../config/game/dishes"
import { getUnitScore } from "../../../core/unit-score"
import { getPortraitSrc } from "../../../utils/avatar"
import { distanceC } from "../../../utils/distance"
import { schemaValues as values } from "../../../utils/schemas"
import { computeSynergies, getSynergyStep } from "../../../models/colyseus-models/synergies"
import { IDetailledPokemon } from "../../../types/models/bot-v2"
import PokemonFactory from "../../../models/pokemon-factory"
import SimulatorState from "../../../rooms/states/simulator-state"
import { Emotion, IDps, PkmWithCustom, SimulatorStatus, Transfer } from "../../../types"
import { Ability } from "../../../types/enum/Ability"
import {
  AbilityPerTM,
  ArtificialItems,
  HerbaMysticas,
  Item,
  Mulches,
  Scarves,
  TMs,
  Wands,
  WeatherRocks
} from "../../../types/enum/Item"
import { DungeonPMDO } from "../../../types/enum/Dungeon"
import { Pkm, PkmIndex } from "../../../types/enum/Pokemon"
import { Synergy } from "../../../types/enum/Synergy"
import {
  SimulatorBattlePreview,
  SimulatorBackgroundTheme,
  SimulatorBoardCell,
  SimulatorFlowerPotSetup,
  SimulatorGroundHoleMode,
  SimulatorGroundHoleSetup,
  SimulatorPreparedFlowerPotPreview,
  SimulatorPreparedGroundHolePreview,
  SimulatorPreparedUnitPreview,
  SimulatorSelectedPokemonDetail,
  SimulatorTeamSetup
} from "../../../types/interfaces/simulator"
import { Weather } from "../../../types/enum/Weather"
import SimulatorContainer from "../game/simulator-container"
import { useAppSelector } from "../hooks"
import { authenticateUser, client, joinSimulator, leaveRoom } from "../network"
import ItemPicker from "./component/bot-builder/item-picker"
import TeamBuilder from "./component/bot-builder/team-builder"
import { MainSidebar } from "./component/main-sidebar/main-sidebar"
import SimulatorBattleSummary from "./component/simulator/simulator-battle-summary"
import Synergies from "./component/synergy/synergies"
import "./simulator.css"

export default function Simulator() {
  const navigate = useNavigate()
  const profile = useAppSelector((state) => state.network.profile)
  const initialShareState = useMemo(() => readInitialSimulatorShareState(), [])
  const [room, setRoom] = useState<Room<SimulatorState>>()
  const [blueTeam, setBlueTeam] = useState<IDetailledPokemon[]>(() =>
    cloneTeam(initialShareState.blue.team)
  )
  const [redTeam, setRedTeam] = useState<IDetailledPokemon[]>(() =>
    cloneTeam(initialShareState.red.team)
  )
  const [stage, setStage] = useState(initialShareState.settings.stage)
  const [weather, setWeather] = useState<Weather>(
    initialShareState.settings.weather
  )
  const [blueSetup, setBlueSetup] = useState<SimulatorTeamSetup>(
    () => cloneSetup(initialShareState.blue.setup)
  )
  const [redSetup, setRedSetup] = useState<SimulatorTeamSetup>(
    () => cloneSetup(initialShareState.red.setup)
  )
  const [backgroundTheme, setBackgroundTheme] =
    useState<SimulatorBackgroundTheme>(initialShareState.settings.background)
  const [status, setStatus] = useState<SimulatorStatus>(SimulatorStatus.IDLE)
  const [result, setResult] = useState<SimulatorState["result"]>()
  const [liveDpsMeters, setLiveDpsMeters] = useState<{
    blue: IDps[]
    red: IDps[]
  }>({
    blue: [],
    red: []
  })
  const [selectedPokemonDetail, setSelectedPokemonDetail] =
    useState<SimulatorSelectedPokemonDetail | null>(null)
  const [blueSelection, setBlueSelection] = useState<Item | PkmWithCustom>(
    createDefaultBuilderSelection
  )
  const [redSelection, setRedSelection] = useState<Item | PkmWithCustom>(
    createDefaultBuilderSelection
  )
  const [blueBuilderTab, setBlueBuilderTab] = useState<"setup" | "items">(
    "items"
  )
  const [redBuilderTab, setRedBuilderTab] = useState<"setup" | "items">(
    "items"
  )
  const [blueTeamLabel, setBlueTeamLabel] = useState("Blue Team")
  const [redTeamLabel, setRedTeamLabel] = useState("Red Team")
  const [itemDragActive, setItemDragActive] = useState(false)
  const [battleViewEnabled, setBattleViewEnabled] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [alertMessage, setAlertMessage] = useState<string | null>(null)
  const initialized = useRef(false)
  const battleRef = useRef<HTMLDivElement>(null)
  const simulatorContainer = useRef<SimulatorContainer | undefined>(undefined)

  const bindRoom = useCallback(
    (simulatorRoom: Room<SimulatorState>) => {
      setConnectError(null)
      setStatus(simulatorRoom.state.status)
      setResult(simulatorRoom.state.result)

      simulatorContainer.current?.destroy()
      if (battleRef.current) {
        simulatorContainer.current = new SimulatorContainer(
          battleRef.current,
          simulatorRoom,
          setAlertMessage,
          setLiveDpsMeters
        )
      }

      simulatorRoom.onLeave(() => {
        setRoom(undefined)
        setLiveDpsMeters({ blue: [], red: [] })
        setSelectedPokemonDetail(null)
        navigate("/lobby")
      })

      simulatorRoom.onError((code, message) => {
        setConnectError(message || `Simulator error (${code})`)
      })

      const $ = getStateCallbacks(simulatorRoom)
      const $state = $(simulatorRoom.state)
      $state.listen("status", (value) => setStatus(value))
      $state.listen("stage", (value) => setStage(value))
      $state.listen("weather", (value) => setWeather(value))
      $state.listen("result", (value) => setResult(value))
    },
    [navigate]
  )

  useEffect(() => {
    if (initialized.current) {
      return
    }

    initialized.current = true
    authenticateUser()
      .then(async () => {
        const idToken = await firebase.auth().currentUser?.getIdToken()
        if (!idToken) {
          navigate("/")
          return
        }

        const simulatorRoom = await client.joinOrCreate<SimulatorState>(
          "simulator",
          { idToken }
        )
        joinSimulator(simulatorRoom)
        setRoom(simulatorRoom)
        bindRoom(simulatorRoom)
      })
      .catch(() => {
        navigate("/")
      })

    return () => {
      simulatorContainer.current?.destroy()
      setSelectedPokemonDetail(null)
      leaveRoom("simulator")
    }
  }, [bindRoom, navigate])

  const leaveSimulator = useCallback(async () => {
    await leaveRoom("simulator")
    setRoom(undefined)
    navigate("/lobby")
  }, [navigate])

  const resetBlueSetup = useCallback(() => {
    setBlueSetup(createDefaultSimulatorSetup())
  }, [])

  const resetRedSetup = useCallback(() => {
    setRedSetup(createDefaultSimulatorSetup())
  }, [])

  const handleBlueSelectionChange = useCallback(
    (selection?: Item | PkmWithCustom) => {
      setSelectedPokemonDetail(buildBuilderSelectionDetail(selection, true))
    },
    []
  )

  const handleRedSelectionChange = useCallback(
    (selection?: Item | PkmWithCustom) => {
      setSelectedPokemonDetail(buildBuilderSelectionDetail(selection, false))
    },
    []
  )

  const running = status === SimulatorStatus.RUNNING
  const canRun =
    !running &&
    countActiveBattlefieldUnits(blueTeam) > 0 &&
    countActiveBattlefieldUnits(redTeam) > 0 &&
    room != null

  const runSimulation = useCallback(() => {
    setAlertMessage(null)
    setResult(undefined)
    setLiveDpsMeters({ blue: [], red: [] })
    room?.send(Transfer.SIMULATOR_RUN, {
      blueTeam: cloneTeam(blueTeam),
      redTeam: cloneTeam(redTeam),
      stage,
      weather,
      setup: {
        blue: blueSetup,
        red: redSetup
      }
    })
  }, [room, blueTeam, redTeam, stage, weather, blueSetup, redSetup])

  const blueTeamPreparation = useMemo(
    () => buildTeamPreparationState(blueTeam),
    [blueTeam]
  )
  const redTeamPreparation = useMemo(
    () => buildTeamPreparationState(redTeam),
    [redTeam]
  )
  const battlePreview = useMemo(
    () => buildBattlePreview(blueTeam, redTeam, blueSetup, redSetup),
    [blueTeam, redTeam, blueSetup, redSetup]
  )
  const summaryDpsMeters = useMemo(
    () => ({
      blue:
        liveDpsMeters.blue.length > 0 || running
          ? liveDpsMeters.blue
          : Array.from(result?.blueUnits ?? []).map(simulatorResultUnitToDps),
      red:
        liveDpsMeters.red.length > 0 || running
          ? liveDpsMeters.red
          : Array.from(result?.redUnits ?? []).map(simulatorResultUnitToDps)
    }),
    [liveDpsMeters, result, running]
  )
  const showSummary =
    running ||
    result != null ||
    liveDpsMeters.blue.length > 0 ||
    liveDpsMeters.red.length > 0
  const showBattleView = battleViewEnabled

  const goNextRound = useCallback(() => {
    setAlertMessage(null)
    setResult(undefined)
    setLiveDpsMeters({ blue: [], red: [] })
    setStatus(SimulatorStatus.IDLE)
    setStage((currentStage) => Math.min(255, currentStage + 1))
    room?.send(Transfer.SIMULATOR_RESET)
  }, [room])

  const stopSimulation = useCallback(() => {
    setAlertMessage(null)
    setResult(undefined)
    setLiveDpsMeters({ blue: [], red: [] })
    setStatus(SimulatorStatus.IDLE)
    room?.send(Transfer.SIMULATOR_RESET)
  }, [room])

  const toggleBattleView = useCallback(() => {
    setBattleViewEnabled((enabled) => !enabled)
  }, [])

  useEffect(() => {
    syncSimulatorShareUrl({
      blue: {
        team: blueTeam,
        setup: blueSetup
      },
      red: {
        team: redTeam,
        setup: redSetup
      },
      settings: {
        stage,
        weather,
        background: backgroundTheme
      }
    })
  }, [blueTeam, redTeam, blueSetup, redSetup, stage, weather, backgroundTheme])

  useEffect(() => {
    simulatorContainer.current?.setPreview(battlePreview)
  }, [battlePreview, room])

  useEffect(() => {
    simulatorContainer.current?.setBackgroundTheme(backgroundTheme)
  }, [backgroundTheme, room])

  useEffect(() => {
    simulatorContainer.current?.refreshLayout()
  }, [showBattleView])

  useEffect(() => {
    const handleDragStart = (event: DragEvent) => {
      const target = event.target
      if (
        target instanceof HTMLElement &&
        target.matches("#item-picker img.item")
      ) {
        setItemDragActive(true)
      }
    }

    const handleDragEnd = () => {
      setItemDragActive(false)
      simulatorContainer.current?.clearDropHover()
    }

    document.addEventListener("dragstart", handleDragStart)
    document.addEventListener("dragend", handleDragEnd)
    document.addEventListener("drop", handleDragEnd)

    return () => {
      document.removeEventListener("dragstart", handleDragStart)
      document.removeEventListener("dragend", handleDragEnd)
      document.removeEventListener("drop", handleDragEnd)
    }
  }, [])

  const handleBattleCanvasDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (running) {
        return
      }

      const dragTypes = Array.from(event.dataTransfer.types ?? [])
      const isItemDrag =
        itemDragActive ||
        dragTypes.includes("application/x-pac-item") ||
        dragTypes.includes("text/plain")

      if (!isItemDrag) {
        return
      }

      event.preventDefault()
      event.dataTransfer.dropEffect = "copy"
      simulatorContainer.current?.updateDropHoverAtClientPoint(
        event.clientX,
        event.clientY
      )
    },
    [itemDragActive, running]
  )

  const handleBattleCanvasDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (running) {
        return
      }

      const item = getDraggedItem(event.dataTransfer)
      if (!item) {
        return
      }

      event.preventDefault()
      const dropTarget =
        simulatorContainer.current?.getDropTargetAtClientPoint(
          event.clientX,
          event.clientY
        )

      if (!dropTarget) {
        return
      }

      if (Mulches.includes(item as (typeof Mulches)[number])) {
        if (dropTarget.kind !== "flowerPot" || dropTarget.index == null) {
          return
        }

        const updateSetup = dropTarget.isAlly ? setBlueSetup : setRedSetup
        let applied = false
        updateSetup((currentSetup) => {
          const nextFlowerPots = applyMulchToFlowerPot(
            currentSetup.flowerPots,
            dropTarget.index!,
            item
          )

          if (!nextFlowerPots) {
            return currentSetup
          }

          applied = true
          return {
            ...currentSetup,
            flowerPots: nextFlowerPots
          }
        })

        if (applied && status !== SimulatorStatus.IDLE) {
          setResult(undefined)
          setLiveDpsMeters({ blue: [], red: [] })
          setStatus(SimulatorStatus.IDLE)
          room?.send(Transfer.SIMULATOR_RESET)
        }

        setItemDragActive(false)
        return
      }

      if (dropTarget.kind !== "pokemon") {
        return
      }

      const updateTeam = dropTarget.isAlly ? setBlueTeam : setRedTeam
      const updateSelection = dropTarget.isAlly
        ? setBlueSelection
        : setRedSelection
      const isAlly = dropTarget.isAlly
      let applied = false

      updateTeam((currentTeam) => {
        const { team: nextTeam, updatedPokemon } = applyItemToDetailedPokemon(
          currentTeam,
          dropTarget.x,
          dropTarget.y,
          item
        )

        if (updatedPokemon) {
          applied = true
          updateSelection(item)
          setSelectedPokemonDetail(
            buildBuilderSelectionDetail(updatedPokemon, isAlly)
          )
        }

        return nextTeam
      })

      if (applied && status !== SimulatorStatus.IDLE) {
        setResult(undefined)
        setLiveDpsMeters({ blue: [], red: [] })
        setStatus(SimulatorStatus.IDLE)
        room?.send(Transfer.SIMULATOR_RESET)
      }

      setItemDragActive(false)
    },
    [room, running, status]
  )

  return (
    <div className="simulator-page">
      <MainSidebar
        page="simulator"
        leave={leaveSimulator}
        leaveLabel="Back to Lobby"
      />
      <main
        className={`simulator-layout${showBattleView ? " focus-mode" : ""}`}
      >
        <section className="simulator-team-column">
          <header>
            <input
              className="simulator-team-name-input"
              value={blueTeamLabel}
              onChange={(event) => setBlueTeamLabel(event.target.value)}
              disabled={running}
              aria-label="Blue team file label"
            />
            <button
              className="bubbly dark"
              disabled={running}
              onClick={() =>
                saveSimulatorBoardFile(
                  blueTeam,
                  blueSetup,
                  buildSimulatorTeamFileName(blueTeamLabel, "simulator-blue-team")
                )
              }
            >
              <img src="assets/ui/save.svg" /> Save
            </button>
            <button
              className="bubbly dark"
              disabled={running}
              onClick={() =>
                loadSimulatorBoardFile(
                  setBlueTeam,
                  setBlueSetup,
                  buildSimulatorTeamFileName(blueTeamLabel, "simulator-blue-team")
                )
              }
            >
              <img src="assets/ui/load.svg" /> Load
            </button>
            <button
              className="bubbly red"
              disabled={running}
              onClick={() => {
                setBlueTeam([])
                resetBlueSetup()
              }}
            >
              Clear Left
            </button>
          </header>
          <div className="simulator-side-panel my-box">
            <div className="simulator-side-tabs" role="tablist" aria-label="Blue team tools">
              <button
                type="button"
                className={`simulator-side-tab${
                  blueBuilderTab === "setup" ? " active" : ""
                }`}
                onClick={() => setBlueBuilderTab("setup")}
                aria-pressed={blueBuilderTab === "setup"}
              >
                Team Setup
              </button>
              <button
                type="button"
                className={`simulator-side-tab${
                  blueBuilderTab === "items" ? " active" : ""
                }`}
                onClick={() => setBlueBuilderTab("items")}
                aria-pressed={blueBuilderTab === "items"}
              >
                Items
              </button>
            </div>
            {blueBuilderTab === "setup" ? (
              <PreBattleSetupCard
                title="Blue Setup"
                setup={blueSetup}
                teamPreparation={blueTeamPreparation}
                onSetupChange={setBlueSetup}
                disabled={running}
              />
            ) : (
              <ItemPicker
                selected={blueSelection}
                selectEntity={setBlueSelection}
                onItemDragStateChange={setItemDragActive}
                showUnholdableItems
              />
            )}
          </div>
          <TeamBuilder
            board={blueTeam}
            updateBoard={setBlueTeam}
            selection={blueSelection}
            onSelectionUpdate={setBlueSelection}
            onSelectionChange={handleBlueSelectionChange}
            emitInitialSelection={false}
            showSelectedEntity={false}
            showActions={false}
            showItemPicker={false}
            highlightedCells={
              buildSetupHighlights(
                blueSetup,
                blueTeamPreparation,
                "Blue"
              )
            }
            disabled={running}
            fileName={buildSimulatorTeamFileName(
              blueTeamLabel,
              "simulator-blue-team"
            )}
            onResetBoard={resetBlueSetup}
            showSynergies={false}
          />
        </section>

        <section className="simulator-center-column">
          <div className="simulator-controls my-box">
            <div className="simulator-controls-top">
              <div className="simulator-controls-copy">
                <h1>Simulator Mode</h1>
                <p>
                  Sandbox battles use the live combat engine with manually built
                  teams and pre-battle setup choices.
                </p>
              </div>
              {showBattleView && (
                <TeamVersusPreview blueTeam={blueTeam} redTeam={redTeam} />
              )}
            </div>
            <div className="simulator-control-selectors">
              <label>
                Weather
                <select
                  value={weather}
                  onChange={(e) => setWeather(e.target.value as Weather)}
                  disabled={running}
                >
                  {Object.values(Weather).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Arena Background
                <select
                  value={backgroundTheme}
                  onChange={(e) =>
                    setBackgroundTheme(e.target.value as SimulatorBackgroundTheme)
                  }
                  disabled={running}
                >
                  {SIMULATOR_BACKGROUND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="simulator-actions">
              <button
                className="bubbly green"
                disabled={!canRun}
                onClick={runSimulation}
              >
                Run
              </button>
              <button
                className="bubbly blue"
                disabled={running || room == null}
                onClick={runSimulation}
              >
                Rerun
              </button>
              <button
                className="bubbly blue"
                disabled={running || room == null || stage >= 255}
                onClick={goNextRound}
              >
                Next Round
              </button>
              <button
                className="bubbly dark"
                type="button"
                onClick={toggleBattleView}
              >
                {showBattleView ? "Exit Battle View" : "Enter Battle View"}
              </button>
              {running && (
                <button
                  className="bubbly red"
                  disabled={room == null}
                  onClick={stopSimulation}
                >
                  Stop Simulation
                </button>
              )}
              <button
                className="bubbly gold"
                type="button"
                onClick={() =>
                  window.open(
                    "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Generate Build
              </button>
            </div>
            <p className={`simulator-status status-${status}`}>
              Status: {status}
            </p>
            {alertMessage && <p className="error">{alertMessage}</p>}
            {connectError && <p className="error">{connectError}</p>}
          </div>

          <section
            className={`simulator-battle-stage${
              showBattleView ? " active" : ""
            }`}
          >
            <div className="simulator-battle my-box">
              <div
                ref={battleRef}
                className="simulator-battle-canvas"
                onDragOver={handleBattleCanvasDragOver}
                onDrop={handleBattleCanvasDrop}
              />
              <div
                className={`simulator-battle-drop-layer${
                  itemDragActive ? " active" : ""
                }`}
                onDragOver={handleBattleCanvasDragOver}
                onDrop={handleBattleCanvasDrop}
              />
            </div>
            {showBattleView && showSummary && (
              <SimulatorBattleSummary
                status={status}
                weather={weather}
                result={result}
                blueDpsMeter={summaryDpsMeters.blue}
                redDpsMeter={summaryDpsMeters.red}
              />
            )}
          </section>

          {!showBattleView && (
            <section className="simulator-synergy-widgets">
              <div className="simulator-synergy-widget my-box">
                <div className="simulator-synergy-widget-header">
                  <h2>Blue Team Synergies</h2>
                </div>
                <Synergies
                  synergies={blueTeamPreparation.synergies}
                  tooltipPortal={false}
                />
              </div>
              <div className="simulator-synergy-widget my-box">
                <div className="simulator-synergy-widget-header">
                  <h2>Red Team Synergies</h2>
                </div>
                <Synergies
                  synergies={redTeamPreparation.synergies}
                  tooltipPortal={false}
                />
              </div>
            </section>
          )}

        </section>

        <section className="simulator-team-column">
          <header>
            <input
              className="simulator-team-name-input red"
              value={redTeamLabel}
              onChange={(event) => setRedTeamLabel(event.target.value)}
              disabled={running}
              aria-label="Red team file label"
            />
            <button
              className="bubbly dark"
              disabled={running}
              onClick={() =>
                saveSimulatorBoardFile(
                  redTeam,
                  redSetup,
                  buildSimulatorTeamFileName(redTeamLabel, "simulator-red-team")
                )
              }
            >
              <img src="assets/ui/save.svg" /> Save
            </button>
            <button
              className="bubbly dark"
              disabled={running}
              onClick={() =>
                loadSimulatorBoardFile(
                  setRedTeam,
                  setRedSetup,
                  buildSimulatorTeamFileName(redTeamLabel, "simulator-red-team")
                )
              }
            >
              <img src="assets/ui/load.svg" /> Load
            </button>
            <button
              className="bubbly red"
              disabled={running}
              onClick={() => {
                setRedTeam([])
                resetRedSetup()
              }}
            >
              Clear Right
            </button>
          </header>
          <div className="simulator-side-panel my-box">
            <div className="simulator-side-tabs" role="tablist" aria-label="Red team tools">
              <button
                type="button"
                className={`simulator-side-tab${
                  redBuilderTab === "setup" ? " active" : ""
                }`}
                onClick={() => setRedBuilderTab("setup")}
                aria-pressed={redBuilderTab === "setup"}
              >
                Team Setup
              </button>
              <button
                type="button"
                className={`simulator-side-tab${
                  redBuilderTab === "items" ? " active" : ""
                }`}
                onClick={() => setRedBuilderTab("items")}
                aria-pressed={redBuilderTab === "items"}
              >
                Items
              </button>
            </div>
            {redBuilderTab === "setup" ? (
              <PreBattleSetupCard
                title="Red Setup"
                setup={redSetup}
                teamPreparation={redTeamPreparation}
                onSetupChange={setRedSetup}
                disabled={running}
              />
            ) : (
              <ItemPicker
                selected={redSelection}
                selectEntity={setRedSelection}
                onItemDragStateChange={setItemDragActive}
                showUnholdableItems
              />
            )}
          </div>
          <TeamBuilder
            board={redTeam}
            updateBoard={setRedTeam}
            selection={redSelection}
            onSelectionUpdate={setRedSelection}
            onSelectionChange={handleRedSelectionChange}
            emitInitialSelection={false}
            showSelectedEntity={false}
            showActions={false}
            showItemPicker={false}
            highlightedCells={
              buildSetupHighlights(redSetup, redTeamPreparation, "Red")
            }
            disabled={running}
            fileName={buildSimulatorTeamFileName(
              redTeamLabel,
              "simulator-red-team"
            )}
            onResetBoard={resetRedSetup}
            showSynergies={false}
          />
        </section>
      </main>
      {!profile && <div className="simulator-loading">Loading profile...</div>}
    </div>
  )
}

function cloneTeam(team: IDetailledPokemon[]) {
  return team.map((unit) => ({
    ...unit,
    items: [...unit.items]
  }))
}

type SimulatorSharedTeamState = {
  team: IDetailledPokemon[]
  setup: SimulatorTeamSetup
}

type SimulatorSharedSettings = {
  stage: number
  weather: Weather
  background: SimulatorBackgroundTheme
}

type SimulatorSharedState = {
  blue: SimulatorSharedTeamState
  red: SimulatorSharedTeamState
  settings: SimulatorSharedSettings
}

type SimulatorSharedTeamPayload = {
  version: 1
  team: IDetailledPokemon[]
  setup: SimulatorTeamSetup
}

type SimulatorSharedSettingsPayload = {
  version: 1
  stage: number
  weather: Weather
  background: SimulatorBackgroundTheme
}

const VALID_TM_ABILITIES = new Set(Object.values(AbilityPerTM))

function createDefaultSimulatorSharedState(): SimulatorSharedState {
  return {
    blue: {
      team: [],
      setup: createDefaultSimulatorSetup()
    },
    red: {
      team: [],
      setup: createDefaultSimulatorSetup()
    },
    settings: {
      stage: 10,
      weather: Weather.NEUTRAL,
      background: DungeonPMDO.BeachCave
    }
  }
}

function readInitialSimulatorShareState(): SimulatorSharedState {
  if (typeof window === "undefined") {
    return createDefaultSimulatorSharedState()
  }

  const defaults = createDefaultSimulatorSharedState()
  const params = new URLSearchParams(window.location.search)
  const blue = parseSimulatorSharedTeamParam(params.get("blue"), defaults.blue)
  const red = parseSimulatorSharedTeamParam(params.get("red"), defaults.red)
  const settings = parseSimulatorSharedSettingsParam(
    params.get("sim"),
    defaults.settings
  )

  return { blue, red, settings }
}

function syncSimulatorShareUrl(state: SimulatorSharedState) {
  if (typeof window === "undefined") {
    return
  }

  const params = new URLSearchParams(window.location.search)
  params.set("blue", encodeSimulatorShareValue(buildSimulatorSharedTeamPayload(state.blue)))
  params.set("red", encodeSimulatorShareValue(buildSimulatorSharedTeamPayload(state.red)))
  params.set(
    "sim",
    encodeSimulatorShareValue({
      version: 1,
      stage: state.settings.stage,
      weather: state.settings.weather,
      background: state.settings.background
    } satisfies SimulatorSharedSettingsPayload)
  )

  const nextSearch = params.toString()
  const currentSearch = window.location.search.startsWith("?")
    ? window.location.search.slice(1)
    : window.location.search

  if (nextSearch === currentSearch) {
    return
  }

  const nextUrl = `${window.location.pathname}${
    nextSearch ? `?${nextSearch}` : ""
  }${window.location.hash}`
  window.history.replaceState(window.history.state, "", nextUrl)
}

function buildSimulatorSharedTeamPayload(
  state: SimulatorSharedTeamState
): SimulatorSharedTeamPayload {
  return {
    version: 1,
    team: cloneTeam(state.team),
    setup: cloneSetup(state.setup)
  }
}

function encodeSimulatorShareValue(value: unknown) {
  const json = JSON.stringify(value)
  const bytes = new TextEncoder().encode(json)
  let binary = ""
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
}

function decodeSimulatorShareValue<T>(value: string): T | null {
  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/")
    const padding = (4 - (normalized.length % 4)) % 4
    const binary = atob(normalized.padEnd(normalized.length + padding, "="))
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes)) as T
  } catch {
    return null
  }
}

function parseSimulatorSharedTeamParam(
  value: string | null,
  fallback: SimulatorSharedTeamState
): SimulatorSharedTeamState {
  if (!value) {
    return {
      team: cloneTeam(fallback.team),
      setup: cloneSetup(fallback.setup)
    }
  }

  const decoded = decodeSimulatorShareValue<Partial<SimulatorSharedTeamPayload>>(value)
  if (!decoded || typeof decoded !== "object") {
    return {
      team: cloneTeam(fallback.team),
      setup: cloneSetup(fallback.setup)
    }
  }

  return {
    team: sanitizeDetailedPokemonArray(decoded.team, fallback.team),
    setup: sanitizeSimulatorTeamSetup(decoded.setup, fallback.setup)
  }
}

function parseSimulatorSharedSettingsParam(
  value: string | null,
  fallback: SimulatorSharedSettings
): SimulatorSharedSettings {
  if (!value) {
    return { ...fallback }
  }

  const decoded =
    decodeSimulatorShareValue<Partial<SimulatorSharedSettingsPayload>>(value)
  if (!decoded || typeof decoded !== "object") {
    return { ...fallback }
  }

  return {
    stage:
      Number.isInteger(decoded.stage) && decoded.stage! >= 1 && decoded.stage! <= 255
        ? decoded.stage!
        : fallback.stage,
    weather: Object.values(Weather).includes(decoded.weather as Weather)
      ? (decoded.weather as Weather)
      : fallback.weather,
    background:
      decoded.background === "town" ||
      Object.values(DungeonPMDO).includes(decoded.background as DungeonPMDO)
        ? (decoded.background as SimulatorBackgroundTheme)
        : fallback.background
  }
}

function sanitizeDetailedPokemonArray(
  team: unknown,
  fallback: IDetailledPokemon[] = []
) {
  if (!Array.isArray(team)) {
    return cloneTeam(fallback)
  }

  return team
    .map((unit) => sanitizeDetailedPokemon(unit))
    .filter((unit): unit is IDetailledPokemon => unit != null)
}

function sanitizeDetailedPokemon(unit: unknown): IDetailledPokemon | null {
  if (unit == null || typeof unit !== "object") {
    return null
  }

  const candidate = unit as Partial<IDetailledPokemon>
  if (!Object.values(Pkm).includes(candidate.name as Pkm)) {
    return null
  }
  if (!Number.isInteger(candidate.x) || candidate.x! < 0 || candidate.x! > 7) {
    return null
  }
  if (!Number.isInteger(candidate.y) || candidate.y! < 0 || candidate.y! > 3) {
    return null
  }
  if (
    !Array.isArray(candidate.items) ||
    candidate.items.length > 3 ||
    !candidate.items.every((item) => Object.values(Item).includes(item))
  ) {
    return null
  }
  if (
    candidate.tm != null &&
    candidate.tm !== Ability.DEFAULT &&
    !VALID_TM_ABILITIES.has(candidate.tm)
  ) {
    return null
  }
  if (
    candidate.supercharged != null &&
    typeof candidate.supercharged !== "boolean"
  ) {
    return null
  }
  if (
    candidate.fireShardCount != null &&
    (!Number.isInteger(candidate.fireShardCount) || candidate.fireShardCount < 0)
  ) {
    return null
  }

  if (
    candidate.smokedFiletCount != null &&
    (!Number.isInteger(candidate.smokedFiletCount) ||
      candidate.smokedFiletCount < 0)
  ) {
    return null
  }

  if (
    candidate.emotion != null &&
    !Object.values(Emotion).includes(candidate.emotion)
  ) {
    return null
  }
  if (candidate.shiny != null && typeof candidate.shiny !== "boolean") {
    return null
  }

  return {
    name: candidate.name as Pkm,
    x: candidate.x as number,
    y: candidate.y as number,
    items: [...candidate.items],
    tm:
      candidate.tm != null && candidate.tm !== Ability.DEFAULT
        ? candidate.tm
        : undefined,
    supercharged: candidate.supercharged === true,
    fireShardCount:
      typeof candidate.fireShardCount === "number" && candidate.fireShardCount > 0
        ? candidate.fireShardCount
        : undefined,
    smokedFiletCount:
      typeof candidate.smokedFiletCount === "number" && candidate.smokedFiletCount > 0
        ? Math.min(candidate.smokedFiletCount, 10)
        : undefined,
    emotion: candidate.emotion ?? Emotion.NORMAL,
    shiny: candidate.shiny ?? false
  }
}

function sanitizeSimulatorTeamSetup(
  setup: unknown,
  fallback: SimulatorTeamSetup
): SimulatorTeamSetup {
  if (setup == null || typeof setup !== "object") {
    return cloneSetup(fallback)
  }

  const candidate = setup as Partial<SimulatorTeamSetup>
  return {
    spotlight: sanitizeSimulatorBoardCell(candidate.spotlight, fallback.spotlight),
    groundHole: sanitizeSimulatorGroundHoleSetup(
      candidate.groundHole,
      fallback.groundHole
    ),
    flowerPots: sanitizeSimulatorFlowerPots(
      candidate.flowerPots,
      fallback.flowerPots
    ),
    fairyWands: sanitizeSimulatorItemList(candidate.fairyWands, Wands, fallback.fairyWands),
    weatherRocks: sanitizeSimulatorItemList(
      candidate.weatherRocks,
      WeatherRocks,
      fallback.weatherRocks
    ),
    artificialItems: sanitizeSimulatorItemList(
      candidate.artificialItems,
      ArtificialItems,
      fallback.artificialItems
    ),
    scarvesItems: sanitizeSimulatorItemList(
      candidate.scarvesItems,
      Scarves,
      fallback.scarvesItems
    )
  }
}

function sanitizeSimulatorItemList(
  items: unknown,
  allowedItems: readonly Item[],
  fallback: Item[]
): Item[] {
  if (!Array.isArray(items)) {
    return [...fallback]
  }

  return items.every((item) => allowedItems.includes(item as Item))
    ? [...(items as Item[])]
    : [...fallback]
}
function sanitizeSimulatorGroundHoleSetup(
  groundHole: unknown,
  fallback: SimulatorGroundHoleSetup
): SimulatorGroundHoleSetup {
  if (groundHole == null || typeof groundHole !== "object") {
    return cloneGroundHoleSetup(fallback)
  }

  const candidate = groundHole as {
    mode?: unknown
    cell?: unknown
    x?: unknown
    y?: unknown
  }
  const mode = isSimulatorGroundHoleMode(candidate.mode)
    ? candidate.mode
    : fallback.mode
  const legacyCell =
    candidate.cell == null &&
    typeof candidate.x === "number" &&
    typeof candidate.y === "number"
      ? { x: candidate.x, y: candidate.y }
      : candidate.cell

  return {
    mode,
    cell: sanitizeSimulatorBoardCell(legacyCell, fallback.cell)
  }
}

function sanitizeSimulatorBoardCell(
  cell: unknown,
  fallback: SimulatorBoardCell
): SimulatorBoardCell {
  if (cell == null || typeof cell !== "object") {
    return { ...fallback }
  }

  const candidate = cell as Partial<SimulatorBoardCell>
  if (!Number.isInteger(candidate.x) || candidate.x! < 0 || candidate.x! > 7) {
    return { ...fallback }
  }
  if (!Number.isInteger(candidate.y) || candidate.y! < 1 || candidate.y! > 3) {
    return { ...fallback }
  }

  return {
    x: candidate.x as number,
    y: candidate.y as number
  }
}

function sanitizeSimulatorFlowerPots(
  flowerPots: unknown,
  fallback: SimulatorFlowerPotSetup[]
) {
  if (!Array.isArray(flowerPots)) {
    return fallback.map((flowerPot) => ({ ...flowerPot }))
  }

  const sanitized = flowerPots
    .map((flowerPot) => sanitizeSimulatorFlowerPot(flowerPot))
    .filter((flowerPot): flowerPot is SimulatorFlowerPotSetup => flowerPot != null)

  return sanitized.length === fallback.length
    ? sanitized
    : fallback.map((flowerPot) => ({ ...flowerPot }))
}

function sanitizeSimulatorFlowerPot(
  flowerPot: unknown
): SimulatorFlowerPotSetup | null {
  if (flowerPot == null || typeof flowerPot !== "object") {
    return null
  }

  const candidate = flowerPot as Partial<SimulatorFlowerPotSetup>
  if (!Object.values(Pkm).includes(candidate.pokemon as Pkm)) {
    return null
  }
  if (
    !Number.isInteger(candidate.maxHPBonus) ||
    !Number.isInteger(candidate.apBonus) ||
    candidate.maxHPBonus! < 0 ||
    candidate.apBonus! < 0
  ) {
    return null
  }

  return {
    pokemon: candidate.pokemon as Pkm,
    maxHPBonus: candidate.maxHPBonus as number,
    apBonus: candidate.apBonus as number
  }
}

function getDraggedItem(dataTransfer: DataTransfer): Item | null {
  const customItem = dataTransfer.getData("application/x-pac-item")
  if (customItem && Object.values(Item).includes(customItem as Item)) {
    return customItem as Item
  }

  const plainText = dataTransfer.getData("text/plain")
  if (!plainText.startsWith("item,")) {
    return null
  }

  const [, item] = plainText.split(",") as [string, Item]
  return Object.values(Item).includes(item) ? item : null
}

function applyItemToDetailedPokemon(
  team: IDetailledPokemon[],
  x: number,
  y: number,
  item: Item
): {
  team: IDetailledPokemon[]
  updatedPokemon?: IDetailledPokemon
} {
  const nextTeam = cloneTeam(team)
  const target = nextTeam.find((unit) => unit.x === x && unit.y === y)

  if (!target) {
    return { team }
  }

  const pokemon = PokemonFactory.createPokemonFromName(target.name, {
    emotion: target.emotion,
    shiny: target.shiny
  })
  pokemon.positionX = target.x
  pokemon.positionY = target.y
  target.items.forEach((heldItem) => pokemon.items.add(heldItem))
  applyDetailedPokemonOverrides(pokemon, target)

  if (TMs.includes(item)) {
    const ability = AbilityPerTM[item]
    if (!ability || !pokemon.types.has(Synergy.HUMAN)) {
      return { team }
    }

    target.tm = ability
  } else if (item === Item.CELL_BATTERY) {
    if (!pokemon.types.has(Synergy.ELECTRIC)) {
      return { team }
    }

    target.supercharged = true
  } else if (item === Item.FIRE_SHARD) {
    if (!pokemon.types.has(Synergy.FIRE)) {
      return { team }
    }

    target.fireShardCount = (target.fireShardCount ?? 0) + 1
  } else if (item === Item.SMOKED_FILET) {
    target.smokedFiletCount = Math.min((target.smokedFiletCount ?? 0) + 1, 10)
  } else if (item === Item.RARE_CANDY) {
    if (target.items.includes(Item.EVIOLITE)) {
      return { team }
    }

    const evolvedName = getRareCandyEvolutionTarget(pokemon)
    if (!evolvedName) {
      return { team }
    }

    target.name = evolvedName
  } else if (target.items.length < 3) {
    target.items.push(item)
  } else {
    target.items = [item]
  }

  return {
    team: nextTeam,
    updatedPokemon: target
  }
}

function countActiveBattlefieldUnits(team: IDetailledPokemon[]) {
  return team.filter((unit) => unit.y > 0).length
}

const DEFAULT_SPOTLIGHT_CELL: SimulatorBoardCell = { x: 3, y: 2 }
const DEFAULT_GROUND_HOLE_CELL: SimulatorBoardCell = { x: 3, y: 2 }

function createDefaultGroundHoleSetup(): SimulatorGroundHoleSetup {
  return {
    mode: "cell",
    cell: { ...DEFAULT_GROUND_HOLE_CELL }
  }
}

function cloneGroundHoleSetup(
  groundHole: SimulatorGroundHoleSetup
): SimulatorGroundHoleSetup {
  return {
    mode: groundHole.mode,
    cell: { ...groundHole.cell }
  }
}

function cloneSetup(setup: SimulatorTeamSetup): SimulatorTeamSetup {
  return {
    spotlight: { ...setup.spotlight },
    groundHole: cloneGroundHoleSetup(setup.groundHole),
    flowerPots: setup.flowerPots.map((flowerPot) => ({ ...flowerPot })),
    fairyWands: [...setup.fairyWands],
    weatherRocks: [...setup.weatherRocks],
    artificialItems: [...setup.artificialItems],
    scarvesItems: [...setup.scarvesItems]
  }
}

function createDefaultSimulatorSetup(): SimulatorTeamSetup {
  return {
    spotlight: { ...DEFAULT_SPOTLIGHT_CELL },
    groundHole: createDefaultGroundHoleSetup(),
    flowerPots: createDefaultFlowerPotSetup(),
    fairyWands: createDefaultFairyWands(),
    weatherRocks: [],
    artificialItems: ArtificialItems.slice(0, 3),
    scarvesItems: []
  }
}

function createDefaultFairyWands(): Item[] {
  return FAIRY_WANDS_BY_SYNERGY_LEVEL.map((tier) => tier[0])
}

function buildBuilderSelectionDetail(
  selection: Item | PkmWithCustom | undefined,
  isAlly: boolean
): SimulatorSelectedPokemonDetail | null {
  if (
    selection == null ||
    Object.keys(Item).includes(selection as Item) ||
    !Object.values(Pkm).includes((selection as PkmWithCustom).name)
  ) {
    return null
  }

  const entity = selection as PkmWithCustom & { items?: Item[] }
  const pokemon = PokemonFactory.createPokemonFromName(entity.name, {
    emotion: entity.emotion,
    shiny: entity.shiny
  })

  entity.items?.forEach((item) => pokemon.items.add(item))
  applyDetailedPokemonOverrides(pokemon, entity as IDetailledPokemon)

  return {
    pokemon,
    origin: "team",
    isAlly,
    version: Date.now()
  }
}

function isSimulatorGroundHoleMode(
  value: unknown
): value is SimulatorGroundHoleMode {
  return (
    value === "cell" ||
    value === "row" ||
    value === "column" ||
    value === "board"
  )
}

function getGroundHoleCells(
  groundHole: SimulatorGroundHoleSetup
): SimulatorBoardCell[] {
  if (groundHole.mode === "board") {
    return [1, 2, 3].flatMap((y) =>
      Array.from({ length: 8 }, (_, x) => ({
        x,
        y
      }))
    )
  }

  if (groundHole.mode === "row") {
    return Array.from({ length: 8 }, (_, x) => ({
      x,
      y: groundHole.cell.y
    }))
  }

  if (groundHole.mode === "column") {
    return [1, 2, 3].map((y) => ({
      x: groundHole.cell.x,
      y
    }))
  }

  return [{ ...groundHole.cell }]
}

function isGroundHoleCellSelected(
  groundHole: SimulatorGroundHoleSetup,
  x: number,
  y: number
) {
  if (groundHole.mode === "board") {
    return true
  }

  if (groundHole.mode === "row") {
    return groundHole.cell.y === y
  }

  if (groundHole.mode === "column") {
    return groundHole.cell.x === x
  }

  return groundHole.cell.x === x && groundHole.cell.y === y
}

function formatGroundHoleSummary(groundHole: SimulatorGroundHoleSetup) {
  if (groundHole.mode === "board") {
    return "Whole Board"
  }

  if (groundHole.mode === "row") {
    return `Row ${groundHole.cell.y}`
  }

  if (groundHole.mode === "column") {
    return `Column ${groundHole.cell.x + 1}`
  }

  return `(${groundHole.cell.x + 1}, ${groundHole.cell.y})`
}

function buildSimulatorTeamFileName(label: string, fallback: string) {
  const sanitized = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

  return `${sanitized || fallback}.json`
}

function PreBattleSetupCard({
  title,
  setup,
  teamPreparation,
  onSetupChange,
  disabled
}: {
  title: string
  setup: SimulatorTeamSetup
  teamPreparation: TeamPreparationState
  onSetupChange: (setup: SimulatorTeamSetup) => void
  disabled: boolean
}) {
  const [lightOpen, setLightOpen] = useState(teamPreparation.lightActive)
  const [groundOpen, setGroundOpen] = useState(teamPreparation.groundActive)
  const [floraOpen, setFloraOpen] = useState(teamPreparation.floraActive)
  const [weatherRocksOpen, setWeatherRocksOpen] = useState(
    teamPreparation.rockActive
  )
  const [fairyWandsOpen, setFairyWandsOpen] = useState(
    teamPreparation.fairyActive
  )
  const generatedItemsActive =
    teamPreparation.artificialActive || teamPreparation.normalActive
  const [generatedItemsOpen, setGeneratedItemsOpen] = useState(
    generatedItemsActive
  )
  const floraPotSlots = getUnlockedFlowerPotCount(
    teamPreparation.floraCount,
    setup.flowerPots
  )

  useEffect(() => {
    if (teamPreparation.lightActive) {
      setLightOpen(true)
    }
  }, [teamPreparation.lightActive])

  useEffect(() => {
    if (teamPreparation.groundActive) {
      setGroundOpen(true)
    }
  }, [teamPreparation.groundActive])

  useEffect(() => {
    if (teamPreparation.floraActive) {
      setFloraOpen(true)
    }
  }, [teamPreparation.floraActive])

  useEffect(() => {
    if (teamPreparation.rockActive) {
      setWeatherRocksOpen(true)
    }
  }, [teamPreparation.rockActive])

  useEffect(() => {
    if (teamPreparation.fairyActive) {
      setFairyWandsOpen(true)
    }
  }, [teamPreparation.fairyActive])

  useEffect(() => {
    if (generatedItemsActive) {
      setGeneratedItemsOpen(true)
    }
  }, [generatedItemsActive])

  function handleFlowerPotDrop(
    event: React.DragEvent<HTMLButtonElement>,
    index: number
  ) {
    event.preventDefault()
    event.stopPropagation()
    if (disabled || index >= floraPotSlots) {
      return
    }

    const data = event.dataTransfer.getData("text/plain")
    if (!data.startsWith("item,")) {
      return
    }

    const [, item] = data.split(",") as [string, Item]
    const nextFlowerPots = applyMulchToFlowerPot(setup.flowerPots, index, item)
    if (!nextFlowerPots) {
      return
    }

    onSetupChange({
      ...setup,
      flowerPots: nextFlowerPots
    })
  }

  function handleFlowerPotDragOver(
    event: React.DragEvent<HTMLButtonElement>,
    index: number
  ) {
    if (index >= floraPotSlots || disabled) {
      return
    }

    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  return (
    <div className="simulator-setup my-box">
      <div className="simulator-setup-header">
        <h3>{title}</h3>
        <span>
          Spotlight: ({setup.spotlight.x + 1}, {setup.spotlight.y}) | Hole:{" "}
          {formatGroundHoleSummary(setup.groundHole)} | Pots: {floraPotSlots}
        </span>
      </div>
      <div className="simulator-setup-meta">
        <span
          className={`simulator-setup-pill ${
            teamPreparation.lightActive ? "active" : "inactive"
          }`}
        >
          Light {teamPreparation.lightCount}/{LIGHT_TRIGGER}
        </span>
        <span
          className={`simulator-setup-pill ${
            teamPreparation.groundActive ? "active" : "inactive"
          }`}
        >
          Ground {teamPreparation.groundCount}/{GROUND_TRIGGER}
        </span>
        <span
          className={`simulator-setup-pill ${
            teamPreparation.floraActive ? "active" : "inactive"
          }`}
        >
          Flora {teamPreparation.floraCount}/{FLORA_TRIGGER}
        </span>
        <span
          className={`simulator-setup-pill ${
            teamPreparation.fairyActive ? "active" : "inactive"
          }`}
        >
          Fairy {teamPreparation.fairyCount}/{FAIRY_TRIGGER}
        </span>
        <span
          className={`simulator-setup-pill ${
            teamPreparation.rockActive ? "active" : "inactive"
          }`}
        >
          Rock {teamPreparation.rockCount}/{ROCK_TRIGGER}
        </span>
        <span
          className={`simulator-setup-pill ${
            teamPreparation.artificialActive ? "active" : "inactive"
          }`}
        >
          Artificial {teamPreparation.artificialCount}/{ARTIFICIAL_TRIGGER}
        </span>
        <span
          className={`simulator-setup-pill ${
            teamPreparation.normalActive ? "active" : "inactive"
          }`}
        >
          Normal {teamPreparation.normalCount}/{NORMAL_TRIGGER}
        </span>
        <span className="simulator-setup-pill">
          Gourmet{" "}
          {teamPreparation.chefHatCount > 0
            ? `T${teamPreparation.gourmetLevel} with ${
                teamPreparation.chefHatCount
              } chef hat${teamPreparation.chefHatCount === 1 ? "" : "s"}`
            : "idle"}
        </span>
      </div>
      <p className="simulator-setup-copy">
        Between-round prep is re-applied on every run, so Gourmet and future
        setup-sensitive items can be adjusted before combat starts.
      </p>

      <div className="simulator-setup-sections">
        <button
          type="button"
          className={`simulator-setup-toggle${
            lightOpen ? " open" : ""
          }${teamPreparation.lightActive ? " active" : " inactive"}`}
          onClick={() => setLightOpen((open) => !open)}
          disabled={disabled}
          aria-expanded={lightOpen}
        >
          <div>
            <strong>Light Spotlight</strong>
            <span>
              {teamPreparation.lightActive
                ? "Ready for the next run."
                : `Saved at (${setup.spotlight.x + 1}, ${setup.spotlight.y}) until Light ${LIGHT_TRIGGER}.`}
            </span>
          </div>
          <span>{lightOpen ? "Hide" : "Show"}</span>
        </button>

        {lightOpen && (
          <div className="simulator-setup-panel">
            <p className="simulator-setup-copy">
              {teamPreparation.lightActive
                ? "Spotlight is armed for the next run."
                : `Spotlight placement is saved, but it only turns on once Light reaches ${LIGHT_TRIGGER}.`}
            </p>
            <div
              className={`simulator-setup-grid${
                teamPreparation.lightActive ? "" : " inactive"
              }`}
              role="grid"
              aria-label={title}
            >
              {[3, 2, 1].map((y) =>
                [0, 1, 2, 3, 4, 5, 6, 7].map((x) => {
                  const active = setup.spotlight.x === x && setup.spotlight.y === y
                  return (
                    <button
                      key={`${title}-${x}-${y}`}
                      type="button"
                      className={active ? "active" : ""}
                      disabled={disabled}
                      aria-label={`Set spotlight to column ${x + 1}, row ${y}`}
                      onClick={() =>
                        onSetupChange({
                          ...setup,
                          spotlight: { x, y }
                        })
                      }
                      aria-pressed={active}
                      title={`Set spotlight to column ${x + 1}, row ${y}${
                        teamPreparation.lightActive ? "" : " (inactive until Light)"
                      }`}
                    />
                  )
                })
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          className={`simulator-setup-toggle${
            groundOpen ? " open" : ""
          }${teamPreparation.groundActive ? " active ground" : " inactive"}`}
          onClick={() => setGroundOpen((open) => !open)}
          disabled={disabled}
          aria-expanded={groundOpen}
        >
          <div>
            <strong>Ground Hole</strong>
            <span>
              {teamPreparation.groundActive
                ? `Depth-5 ${formatGroundHoleSummary(setup.groundHole).toLowerCase()} is ready for the next run.`
                : `Saved ${formatGroundHoleSummary(setup.groundHole).toLowerCase()} until Ground ${GROUND_TRIGGER}.`}
            </span>
          </div>
          <span>{groundOpen ? "Hide" : "Show"}</span>
        </button>

        {groundOpen && (
          <div className="simulator-setup-panel simulator-setup-panel-ground">
            <p className="simulator-setup-copy">
              {teamPreparation.groundActive
                ? "Pick a single cell, an entire row, or an entire column to start as depth-5 holes."
                : `Ground digging is saved, but it only turns on once Ground reaches ${GROUND_TRIGGER}.`}
            </p>
            <div className="simulator-setup-mode-row">
              {(
                ["cell", "row", "column", "board"] as SimulatorGroundHoleMode[]
              ).map(
                (mode) => (
                  <button
                    key={`${title}-ground-mode-${mode}`}
                    type="button"
                    className={
                      setup.groundHole.mode === mode ? "active" : ""
                    }
                    disabled={disabled}
                    onClick={() =>
                      onSetupChange({
                        ...setup,
                        groundHole: {
                          ...setup.groundHole,
                          mode
                        }
                      })
                    }
                    aria-pressed={setup.groundHole.mode === mode}
                  >
                    {mode === "cell"
                      ? "Single Hole"
                      : mode === "row"
                        ? "Full Row"
                        : mode === "column"
                          ? "Full Column"
                          : "Whole Board"}
                  </button>
                )
              )}
            </div>
            <div
              className={`simulator-setup-grid${
                teamPreparation.groundActive ? "" : " inactive"
              }`}
              role="grid"
              aria-label={`${title} ground hole`}
            >
              {[3, 2, 1].map((y) =>
                [0, 1, 2, 3, 4, 5, 6, 7].map((x) => {
                  const active = isGroundHoleCellSelected(setup.groundHole, x, y)
                  return (
                    <button
                      key={`${title}-ground-${x}-${y}`}
                      type="button"
                      className={active ? "active ground" : "ground"}
                      disabled={disabled}
                      aria-label={`Set ground ${setup.groundHole.mode} to column ${x + 1}, row ${y}`}
                      onClick={() =>
                        onSetupChange({
                          ...setup,
                          groundHole: {
                            ...setup.groundHole,
                            cell: { x, y }
                          }
                        })
                      }
                      aria-pressed={active}
                      title={`Set ground ${setup.groundHole.mode} to column ${x + 1}, row ${y}${
                        teamPreparation.groundActive
                          ? " (level 5)"
                          : " (inactive until Ground)"
                      }`}
                    />
                  )
                })
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          className={`simulator-setup-toggle${
            floraOpen ? " open" : ""
          }${teamPreparation.floraActive ? " active flora" : " inactive"}`}
          onClick={() => setFloraOpen((open) => !open)}
          disabled={disabled}
          aria-expanded={floraOpen}
        >
          <div>
            <strong>Flora Pots</strong>
            <span>
              {teamPreparation.floraActive
                ? `Drag mulch onto the unlocked pots. ${floraPotSlots} pot${
                    floraPotSlots === 1 ? "" : "s"
                  } ready.`
                : `Saved Flora setup wakes up at Flora ${FLORA_TRIGGER}.`}
            </span>
          </div>
          <span>{floraOpen ? "Hide" : "Show"}</span>
        </button>

        {floraOpen && (
          <div className="simulator-setup-panel simulator-setup-panel-flora">
            <p className="simulator-setup-copy">
              {teamPreparation.floraActive
                ? "Rich Mulch evolves the flower in that pot. Amaze Mulch permanently adds +50 HP and +30 AP to the flower pot Pokemon."
                : `Flora pot setup is saved, but it only turns on once Flora reaches ${FLORA_TRIGGER}.`}
            </p>
            <div className="simulator-flora-pots" role="list">
              {setup.flowerPots.map((flowerPot, index) => {
                const unlocked = index < floraPotSlots
                const pokemon = PokemonFactory.createPokemonFromName(
                  flowerPot.pokemon
                )
                const canEvolve = pokemon.evolution !== Pkm.DEFAULT
                return (
                  <button
                    key={`${title}-flora-${index}`}
                    type="button"
                    className={`simulator-flora-pot${
                      unlocked ? " unlocked" : " locked"
                    }${flowerPot.maxHPBonus > 0 || flowerPot.apBonus > 0 ? " buffed" : ""}`}
                    disabled={disabled}
                    onDragOver={(event) => handleFlowerPotDragOver(event, index)}
                    onDrop={(event) => handleFlowerPotDrop(event, index)}
                    title={
                      unlocked
                        ? `${flowerPot.pokemon}${
                            canEvolve ? ` -> ${pokemon.evolution}` : " (fully grown)"
                          }`
                        : "Locked until more Flora is active"
                    }
                  >
                    <span className="simulator-flora-pot-label">
                      Pot {index + 1}
                    </span>
                    <strong>{flowerPot.pokemon.split("_").join(" ")}</strong>
                    <span className="simulator-flora-pot-copy">
                      {unlocked
                        ? canEvolve
                          ? `Next: ${pokemon.evolution.split("_").join(" ")}`
                          : "Fully grown"
                        : "Locked"}
                    </span>
                    <span className="simulator-flora-pot-bonuses">
                      +{flowerPot.maxHPBonus} HP / +{flowerPot.apBonus} AP
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
        <button
          type="button"
          className={`simulator-setup-toggle${
            weatherRocksOpen ? " open" : ""
          }${teamPreparation.rockActive ? " active items" : " inactive"}`}
          onClick={() => setWeatherRocksOpen((open) => !open)}
          disabled={disabled}
          aria-expanded={weatherRocksOpen}
        >
          <div>
            <strong>Weather Rocks</strong>
            <span>
              {teamPreparation.rockActive
                ? `${teamPreparation.weatherRockSlots}/3 slots unlocked.`
                : `Saved until Rock ${ROCK_TRIGGER}.`}
            </span>
          </div>
          <span>{weatherRocksOpen ? "Hide" : "Show"}</span>
        </button>

        {weatherRocksOpen && (
          <div className="simulator-setup-panel simulator-setup-panel-items">
            <SimulatorIconPoolSelector
              title="Weather Rocks"
              items={WeatherRocks}
              selectedItems={setup.weatherRocks}
              activeSlots={teamPreparation.weatherRockSlots}
              maxSlots={3}
              disabled={disabled || !teamPreparation.rockActive}
              emptyMessage={`Unlocks at Rock ${ROCK_TRIGGER}.`}
              onChange={(weatherRocks) =>
                onSetupChange({
                  ...setup,
                  weatherRocks
                })
              }
            />
          </div>
        )}

        <button
          type="button"
          className={`simulator-setup-toggle${
            fairyWandsOpen ? " open" : ""
          }${teamPreparation.fairyActive ? " active items" : " inactive"}`}
          onClick={() => setFairyWandsOpen((open) => !open)}
          disabled={disabled}
          aria-expanded={fairyWandsOpen}
        >
          <div>
            <strong>Fairy Wands</strong>
            <span>
              {teamPreparation.fairyActive
                ? `${teamPreparation.fairyWandSlots}/4 slots unlocked.`
                : `Saved until Fairy ${FAIRY_TRIGGER}.`}
            </span>
          </div>
          <span>{fairyWandsOpen ? "Hide" : "Show"}</span>
        </button>

        {fairyWandsOpen && (
          <div className="simulator-setup-panel simulator-setup-panel-items">
            <SimulatorIconPoolSelector
              title="Fairy Wands"
              items={Wands}
              optionRows={chunkItems(Wands, 8)}
              selectedItems={setup.fairyWands}
              activeSlots={teamPreparation.fairyWandSlots}
              maxSlots={4}
              disabled={disabled || !teamPreparation.fairyActive}
              emptyMessage={`Unlocks at Fairy ${FAIRY_TRIGGER}.`}
              onChange={(fairyWands) =>
                onSetupChange({
                  ...setup,
                  fairyWands
                })
              }
            />
          </div>
        )}

        <button
          type="button"
          className={`simulator-setup-toggle${
            generatedItemsOpen ? " open" : ""
          }${generatedItemsActive ? " active items" : " inactive"}`}
          onClick={() => setGeneratedItemsOpen((open) => !open)}
          disabled={disabled}
          aria-expanded={generatedItemsOpen}
        >
          <div>
            <strong>Generated Items</strong>
            <span>
              Artificial tools and Normal scarves granted by synergy levels.
            </span>
          </div>
          <span>{generatedItemsOpen ? "Hide" : "Show"}</span>
        </button>

        {generatedItemsOpen && (
          <div className="simulator-setup-panel simulator-setup-panel-items">
            <div className="simulator-icon-pool-stack">
              <SimulatorIconPoolSelector
                title="Artificial Items"
                items={ArtificialItems}
                selectedItems={setup.artificialItems}
                activeSlots={teamPreparation.artificialItemSlots}
                maxSlots={3}
                disabled={disabled || !teamPreparation.artificialActive}
                emptyMessage={`Unlocks at Artificial ${ARTIFICIAL_TRIGGER}.`}
                onChange={(artificialItems) =>
                  onSetupChange({
                    ...setup,
                    artificialItems
                  })
                }
              />
              <SimulatorIconPoolSelector
                title="Normal Scarves"
                items={Scarves}
                selectedItems={setup.scarvesItems}
                activeSlots={teamPreparation.scarfSlots}
                maxSlots={4}
                disabled={disabled || !teamPreparation.normalActive}
                emptyMessage={`Unlocks at Normal ${NORMAL_TRIGGER}.`}
                onChange={(scarvesItems) =>
                  onSetupChange({
                    ...setup,
                    scarvesItems
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

type SimulatorIconPoolSelectorProps = {
  title: string
  items: readonly Item[]
  optionRows?: readonly (readonly Item[])[]
  optionsBySlot?: readonly (readonly Item[])[]
  selectedItems: Item[]
  activeSlots: number
  maxSlots: number
  disabled: boolean
  emptyMessage: string
  onChange: (items: Item[]) => void
}

function SimulatorIconPoolSelector({
  title,
  items,
  optionRows,
  optionsBySlot,
  selectedItems,
  activeSlots,
  maxSlots,
  disabled,
  emptyMessage,
  onChange
}: SimulatorIconPoolSelectorProps) {
  const visibleOptions = optionRows ?? chunkItems(items, 8)
  const normalizedItems = Array.from({ length: maxSlots }, (_, index) => {
    const current = selectedItems[index]
    const slotOptions = optionsBySlot?.[index] ?? items
    return current != null && slotOptions.includes(current) ? current : undefined
  })

  function getAvailableSlotIndex(item: Item) {
    for (let index = 0; index < activeSlots && index < maxSlots; index++) {
      const slotOptions = optionsBySlot?.[index] ?? items
      if (slotOptions.includes(item) && normalizedItems[index] == null) {
        return index
      }
    }

    for (let index = 0; index < activeSlots && index < maxSlots; index++) {
      const slotOptions = optionsBySlot?.[index] ?? items
      if (slotOptions.includes(item)) {
        return index
      }
    }

    return -1
  }

  function handleItemClick(item: Item) {
    if (disabled) {
      return
    }

    const slotIndex = getAvailableSlotIndex(item)
    if (slotIndex < 0) {
      return
    }

    const nextItems = normalizedItems.slice()
    nextItems[slotIndex] = item
    onChange(compactSetupItems(nextItems))
  }

  function handleSlotClick(index: number) {
    if (disabled || index >= activeSlots) {
      return
    }

    const nextItems = normalizedItems.slice()
    nextItems[index] = undefined
    onChange(compactSetupItems(nextItems))
  }

  return (
    <div className="simulator-icon-pool">
      <div className="simulator-icon-pool-title">
        <strong>{title}</strong>
        <span>
          {activeSlots > 0
            ? `${activeSlots}/${maxSlots} slots unlocked`
            : emptyMessage}
        </span>
      </div>
      <div className="simulator-icon-pool-options" aria-label={`${title} options`}>
        {visibleOptions.map((row, rowIndex) => (
          <div className="simulator-icon-pool-option-row" key={`${title}-${rowIndex}`}>
            {row.map((item) => {
              const selectable = getAvailableSlotIndex(item) >= 0
              const selected = normalizedItems.includes(item)
              return (
                <button
                  type="button"
                  key={item}
                  className={`simulator-icon-pool-option${
                    selected ? " selected" : ""
                  }${selectable ? "" : " locked"}`}
                  disabled={disabled || !selectable}
                  onClick={() => handleItemClick(item)}
                  title={formatSetupItemLabel(item)}
                  aria-label={`Select ${formatSetupItemLabel(item)}`}
                >
                  <img src={`assets/item/${Item[item]}.png`} alt="" />
                </button>
              )
            })}
          </div>
        ))}
      </div>
      <div className={`simulator-icon-pool-slots slots-${maxSlots}`} aria-label={`${title} slots`}>
        {normalizedItems.map((item, index) => {
          const unlocked = index < activeSlots
          return (
            <button
              type="button"
              key={`${title}-slot-${index}`}
              className={`simulator-icon-pool-slot${
                unlocked ? " unlocked" : " locked"
              }${item ? " filled" : ""}`}
              disabled={disabled || !unlocked}
              onClick={() => handleSlotClick(index)}
              title={
                item
                  ? `Clear ${formatSetupItemLabel(item)}`
                  : unlocked
                    ? "Empty unlocked slot"
                    : "Locked by synergy level"
              }
              aria-label={`Slot ${index + 1}`}
            >
              {item && <img src={`assets/item/${Item[item]}.png`} alt="" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function compactSetupItems(items: (Item | undefined)[]) {
  return items.filter((item): item is Item => item != null)
}

function chunkItems<T>(items: readonly T[], size: number) {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

function formatSetupItemLabel(item: Item) {
  return item
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ")
}
type TeamPreparationState = {
  lightActive: boolean
  lightCount: number
  groundActive: boolean
  groundCount: number
  floraActive: boolean
  floraCount: number
  fairyActive: boolean
  fairyCount: number
  fairyWandSlots: number
  rockActive: boolean
  rockCount: number
  weatherRockSlots: number
  artificialActive: boolean
  artificialCount: number
  artificialItemSlots: number
  normalActive: boolean
  normalCount: number
  scarfSlots: number
  gourmetLevel: number
  chefHatCount: number
  synergies: [string, number][]
}

type PreviewPokemon = ReturnType<typeof PokemonFactory.createPokemonFromName>

const LIGHT_TRIGGER = SynergyTriggers[Synergy.LIGHT][0]
const GROUND_TRIGGER = SynergyTriggers[Synergy.GROUND][0]
const FLORA_TRIGGER = SynergyTriggers[Synergy.FLORA][0]
const FAIRY_TRIGGER = SynergyTriggers[Synergy.FAIRY][0]
const ROCK_TRIGGER = SynergyTriggers[Synergy.ROCK][0]
const ARTIFICIAL_TRIGGER = SynergyTriggers[Synergy.ARTIFICIAL][0]
const NORMAL_TRIGGER = SynergyTriggers[Synergy.NORMAL][0]
const FAIRY_WAND_SLOTS_BY_LEVEL = [0, 1, 2, 3, 4] as const
const GROUND_HOLE_DEFENSE_PER_LEVEL = [0, 1, 2, 3, 3] as const
const GROUND_HOLE_ATTACK_ON_LEVEL_5 = [0, 3, 5, 8, 8] as const
const SIMULATOR_BACKGROUND_OPTIONS: {
  value: SimulatorBackgroundTheme
  label: string
}[] = Object.values(DungeonPMDO)
  .filter(isNormalBattleBackground)
  .map((value) => ({
    value,
    label: formatSimulatorBackgroundLabel(value)
  }))
  .sort((a, b) => a.label.localeCompare(b.label))

function isNormalBattleBackground(value: DungeonPMDO) {
  return !value.includes("Maze") && !value.includes("Unused")
}

function formatSimulatorBackgroundLabel(value: DungeonPMDO) {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .replace(/(\d)([A-Za-z])/g, "$1 $2")
    .replace(/^Mt /, "Mt. ")
}

function simulatorResultUnitToDps(unit: {
  id: string
  name: string
  physicalDamage: number
  specialDamage: number
  trueDamage: number
  physicalDamageReduced: number
  specialDamageReduced: number
  shieldDamageTaken: number
  heal: number
  shieldDone: number
}): IDps {
  return {
    update() {},
    id: unit.id,
    name: unit.name,
    physicalDamage: unit.physicalDamage,
    specialDamage: unit.specialDamage,
    trueDamage: unit.trueDamage,
    physicalDamageReduced: unit.physicalDamageReduced,
    specialDamageReduced: unit.specialDamageReduced,
    shieldDamageTaken: unit.shieldDamageTaken,
    heal: unit.heal,
    shield: unit.shieldDone
  }
}

function buildTeamPreparationState(
  team: IDetailledPokemon[]
): TeamPreparationState {
  const board = team.map((unit) => {
    const pokemon = PokemonFactory.createPokemonFromName(unit.name, {
      emotion: unit.emotion,
      shiny: unit.shiny
    })
    pokemon.positionX = unit.x
    pokemon.positionY = unit.y
    unit.items.forEach((item) => pokemon.items.add(item))
    applyDetailedPokemonOverrides(pokemon, unit)
    return pokemon
  })
  const synergies = computeSynergies(board)
  const lightCount = synergies.get(Synergy.LIGHT) ?? 0
  const groundCount = synergies.get(Synergy.GROUND) ?? 0
  const floraCount = synergies.get(Synergy.FLORA) ?? 0
  const fairyCount = synergies.get(Synergy.FAIRY) ?? 0
  const rockCount = synergies.get(Synergy.ROCK) ?? 0
  const artificialCount = synergies.get(Synergy.ARTIFICIAL) ?? 0
  const normalCount = synergies.get(Synergy.NORMAL) ?? 0
  const fairyLevel = getSynergyStep(synergies, Synergy.FAIRY)
  const rockLevel = getSynergyStep(synergies, Synergy.ROCK)
  const artificialItemSlots = SynergyTriggers[Synergy.ARTIFICIAL].filter(
    (threshold) => artificialCount >= threshold
  ).length
  const scarfSlots = getSynergyStep(synergies, Synergy.NORMAL)

  return {
    lightActive: lightCount >= LIGHT_TRIGGER,
    lightCount,
    groundActive: groundCount >= GROUND_TRIGGER,
    groundCount,
    floraActive: floraCount >= FLORA_TRIGGER,
    floraCount,
    fairyActive: fairyCount >= FAIRY_TRIGGER,
    fairyCount,
    fairyWandSlots: FAIRY_WAND_SLOTS_BY_LEVEL[fairyLevel] ?? 0,
    rockActive: rockCount >= ROCK_TRIGGER,
    rockCount,
    weatherRockSlots: rockLevel,
    artificialActive: artificialCount >= ARTIFICIAL_TRIGGER,
    artificialCount,
    artificialItemSlots,
    normalActive: normalCount >= NORMAL_TRIGGER,
    normalCount,
    scarfSlots,
    gourmetLevel: getSynergyStep(synergies, Synergy.GOURMET),
    chefHatCount: board.filter(
      (pokemon) => pokemon.positionY > 0 && pokemon.items.has(Item.CHEF_HAT)
    ).length,
    synergies: [...synergies.entries()]
  }
}

function buildBattlePreview(
  blueTeam: IDetailledPokemon[],
  redTeam: IDetailledPokemon[],
  blueSetup: SimulatorTeamSetup,
  redSetup: SimulatorTeamSetup
): SimulatorBattlePreview {
  return {
    blue: buildPreparedPreviewTeam(blueTeam, blueSetup),
    red: buildPreparedPreviewTeam(redTeam, redSetup),
    blueGroundHoles: buildPreparedGroundHolePreview(blueTeam, blueSetup),
    redGroundHoles: buildPreparedGroundHolePreview(redTeam, redSetup),
    blueFlowerPots: buildPreparedFlowerPotPreview(blueTeam, blueSetup),
    redFlowerPots: buildPreparedFlowerPotPreview(redTeam, redSetup)
  }
}

function buildPreparedPreviewTeam(
  team: IDetailledPokemon[],
  setup: SimulatorTeamSetup
): SimulatorPreparedUnitPreview[] {
  const boardEntries = team
    .filter((unit) => unit.y > 0)
    .map((unit) => {
      const pokemon = PokemonFactory.createPokemonFromName(unit.name, {
        emotion: unit.emotion,
        shiny: unit.shiny
      })
      pokemon.id = `${unit.name}-${unit.x}-${unit.y}-${unit.items.join("-")}`
      pokemon.positionX = unit.x
      pokemon.positionY = unit.y
      unit.items.forEach((item) => pokemon.items.add(item))
      applyDetailedPokemonOverrides(pokemon, unit)
      pokemon.dishes.clear()
      return { pokemon, detail: unit }
    })
  const board = boardEntries.map((entry) => entry.pokemon)
  const detailById = new Map(
    boardEntries.map((entry) => [entry.pokemon.id, entry.detail])
  )

  const synergies = computeSynergies(board)
  const lightActive = (synergies.get(Synergy.LIGHT) ?? 0) >= LIGHT_TRIGGER
  const groundLevel = getSynergyStep(synergies, Synergy.GROUND)
  const groundHoleCells = getGroundHoleCells(setup.groundHole)
  const gourmetLevel = getSynergyStep(synergies, Synergy.GOURMET)

  const previews = board.map((pokemon) => {
    const detail = detailById.get(pokemon.id)
    const groundHoleBonus = buildGroundHolePreviewBonus(
      pokemon,
      groundHoleCells,
      groundLevel
    )
    return {
      id: pokemon.id,
      name: pokemon.name,
      x: pokemon.positionX,
      y: pokemon.positionY,
      items: values(pokemon.items),
      tm: pokemon.tm !== Ability.DEFAULT ? pokemon.tm : undefined,
      supercharged: detail?.supercharged ?? false,
      fireShardCount: detail?.fireShardCount ?? 0,
      smokedFiletCount: detail?.smokedFiletCount ?? 0,
      groundHoleAttackBonus: groundHoleBonus.attack,
      groundHoleDefenseBonus: groundHoleBonus.defense,
      dishes: [] as Item[],
      shiny: pokemon.shiny,
      emotion: pokemon.emotion,
      spotlight:
        lightActive &&
        pokemon.positionX === setup.spotlight.x &&
        pokemon.positionY === setup.spotlight.y
    }
  })

  const previewById = new Map(previews.map((preview) => [preview.id, preview]))
  const chefs = board.filter((pokemon) => pokemon.items.has(Item.CHEF_HAT))

  chefs.forEach((chef) => {
    const dishes = buildPreviewChefDishes(chef, gourmetLevel)
    dishes.forEach((dish) => {
      const target = selectPreviewDishTarget(board, chef, dish)
      const preview = previewById.get(target.id)
      if (preview) {
        preview.dishes.push(dish)
      }
    })
  })

  return previews
}

function buildPreparedGroundHolePreview(
  team: IDetailledPokemon[],
  setup: SimulatorTeamSetup
): SimulatorPreparedGroundHolePreview[] {
  const board = team.map((unit) => {
    const pokemon = PokemonFactory.createPokemonFromName(unit.name, {
      emotion: unit.emotion,
      shiny: unit.shiny
    })
    pokemon.positionX = unit.x
    pokemon.positionY = unit.y
    unit.items.forEach((item) => pokemon.items.add(item))
    applyDetailedPokemonOverrides(pokemon, unit)
    return pokemon
  })
  const synergies = computeSynergies(board)
  const groundActive = (synergies.get(Synergy.GROUND) ?? 0) >= GROUND_TRIGGER

  return groundActive
    ? getGroundHoleCells(setup.groundHole).map((cell) => ({
        x: cell.x,
        y: cell.y,
        level: 5
      }))
    : []
}

function buildGroundHolePreviewBonus(
  pokemon: PreviewPokemon,
  groundHoles: SimulatorBoardCell[],
  groundLevel: number
) {
  if (groundLevel <= 0 || pokemon.types.has(Synergy.GROUND) === false) {
    return { attack: 0, defense: 0 }
  }

  const onGroundHole = groundHoles.some(
    (groundHole) =>
      pokemon.positionX === groundHole.x && pokemon.positionY === groundHole.y
  )
  const fullyDugRows =
    groundLevel === 4 ? countFullyDugGroundRows(groundHoles) : 0
  let attack = 0
  let defense = fullyDugRows * 5

  if (onGroundHole) {
    attack = GROUND_HOLE_ATTACK_ON_LEVEL_5[groundLevel] ?? 0
    defense += 5 * (GROUND_HOLE_DEFENSE_PER_LEVEL[groundLevel] ?? 0)
  }

  if (groundLevel === 4 && fullyDugRows === 3) {
    attack += 8
  }

  return { attack, defense }
}

function buildPreparedFlowerPotPreview(
  team: IDetailledPokemon[],
  setup: SimulatorTeamSetup
): SimulatorPreparedFlowerPotPreview[] {
  const board = team.map((unit) => {
    const pokemon = PokemonFactory.createPokemonFromName(unit.name, {
      emotion: unit.emotion,
      shiny: unit.shiny
    })
    pokemon.positionX = unit.x
    pokemon.positionY = unit.y
    unit.items.forEach((item) => pokemon.items.add(item))
    applyDetailedPokemonOverrides(pokemon, unit)
    return pokemon
  })
  const synergies = computeSynergies(board)
  const floraLevel = synergies.get(Synergy.FLORA) ?? 0
  const nbPots = getUnlockedFlowerPotCount(floraLevel, setup.flowerPots)

  return setup.flowerPots.slice(0, nbPots).map((flowerPot) => ({
    pokemon: flowerPot.pokemon,
    maxHPBonus: flowerPot.maxHPBonus,
    apBonus: flowerPot.apBonus
  }))
}

function createDefaultBuilderSelection(): PkmWithCustom {
  return {
    name: Pkm.MAGIKARP,
    shiny: false,
    emotion: Emotion.NORMAL
  }
}

function saveSimulatorBoardFile(
  team: IDetailledPokemon[],
  setup: SimulatorTeamSetup,
  fileName: string
) {
  const blob = new Blob(
    [
      JSON.stringify(
        buildSimulatorSharedTeamPayload({
          team,
          setup
        })
      )
    ],
    { type: "application/json" }
  )
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(url)
}

function loadSimulatorBoardFile(
  updateBoard: (board: IDetailledPokemon[]) => void,
  updateSetup: (setup: SimulatorTeamSetup) => void,
  fileName: string
) {
  const input = document.createElement("input")
  input.type = "file"
  input.accept = "application/json"
  input.addEventListener("change", async () => {
    if (!input.files?.[0]) {
      return
    }

    const file = input.files[0]
    const reader = new FileReader()
    reader.onload = async (event) => {
      if (!event.target?.result) {
        return
      }

      try {
        const data = JSON.parse(event.target.result as string)
        if (Array.isArray(data)) {
          updateBoard(sanitizeDetailedPokemonArray(data))
          return
        }

        const teamState = parseSimulatorSharedTeamParam(
          encodeSimulatorShareValue(data),
          {
            team: [],
            setup: createDefaultSimulatorSetup()
          }
        )
        updateBoard(teamState.team)
        updateSetup(teamState.setup)
      } catch {
        alert(`Invalid file for ${fileName}`)
      }
    }
    reader.readAsText(file)
  })
  input.click()
}

function TeamVersusPreview({
  blueTeam,
  redTeam
}: {
  blueTeam: IDetailledPokemon[]
  redTeam: IDetailledPokemon[]
}) {
  const blueRoster = getTeamPreviewRoster(blueTeam)
  const redRoster = getTeamPreviewRoster(redTeam)

  return (
    <div className="simulator-versus-preview">
      <div className="simulator-versus-team blue">
        {blueRoster.map((unit) => (
          <img
            key={`blue-${unit.x}-${unit.y}-${unit.name}`}
            src={getPortraitSrc(PkmIndex[unit.name], unit.shiny, unit.emotion)}
            alt={unit.name}
            title={unit.name.split("_").join(" ")}
          />
        ))}
      </div>
      <span className="simulator-versus-label">VS</span>
      <div className="simulator-versus-team red">
        {redRoster.map((unit) => (
          <img
            key={`red-${unit.x}-${unit.y}-${unit.name}`}
            src={getPortraitSrc(PkmIndex[unit.name], unit.shiny, unit.emotion)}
            alt={unit.name}
            title={unit.name.split("_").join(" ")}
          />
        ))}
      </div>
    </div>
  )
}

function getTeamPreviewRoster(team: IDetailledPokemon[]) {
  return team
    .filter((unit) => unit.y > 0)
    .sort((a, b) => a.y - b.y || a.x - b.x)
}

function buildSetupHighlights(
  setup: SimulatorTeamSetup,
  teamPreparation: TeamPreparationState,
  teamLabel: string
) {
  const highlights = new Map<
    string,
    { x: number; y: number; className: string; title: string }
  >()

  const addHighlight = (
    cell: SimulatorBoardCell,
    className: string,
    title: string
  ) => {
    const key = `${cell.x},${cell.y}`
    const existing = highlights.get(key)
    if (existing) {
      existing.className = `${existing.className} ${className}`
      existing.title = `${existing.title} + ${title}`
      return
    }

    highlights.set(key, {
      ...cell,
      className,
      title
    })
  }

  if (teamPreparation.lightActive) {
    addHighlight(setup.spotlight, "team-editor-spotlight", `${teamLabel} spotlight`)
  }

  if (teamPreparation.groundActive) {
    getGroundHoleCells(setup.groundHole).forEach((groundHole) => {
      addHighlight(
        groundHole,
        "team-editor-ground-hole",
        `${teamLabel} level 5 ground hole`
      )
    })
  }

  return Array.from(highlights.values())
}

function countFullyDugGroundRows(groundHoles: SimulatorBoardCell[]) {
  const cells = new Set(groundHoles.map((groundHole) => `${groundHole.x},${groundHole.y}`))
  return [1, 2, 3].filter((y) =>
    Array.from({ length: 8 }, (_, x) => cells.has(`${x},${y}`)).every(Boolean)
  ).length
}

function buildPreviewChefDishes(
  chef: PreviewPokemon,
  gourmetLevel: number
): Item[] {
  const nbDishes = [0, 1, 2, 2][gourmetLevel] ?? 2
  let dish = DishByPkm[chef.name] as Item | null | undefined

  if (chef.items.has(Item.COOKING_POT)) {
    dish = Item.HEARTY_STEW
  } else if (
    chef.name.startsWith("ARCEUS") ||
    chef.name === Pkm.KECLEON ||
    chef.items.has(Item.GOURMET_MEMORY)
  ) {
    dish = Item.SANDWICH
  }

  if (!dish || nbDishes <= 0) {
    return []
  }

  return Array.from({ length: nbDishes }, () => dish!)
}

function selectPreviewDishTarget(
  board: PreviewPokemon[],
  chef: PreviewPokemon,
  dish: Item
) {
  let candidates = board.filter(
    (pokemon) =>
      pokemon.canEat &&
      !pokemon.dishes.has(dish) &&
      distanceC(
        chef.positionX,
        chef.positionY,
        pokemon.positionX,
        pokemon.positionY
      ) === 1
  )

  if (dish === Item.HERBA_MYSTICA) {
    candidates = candidates.filter((pokemon) =>
      HerbaMysticas.every((herba) => pokemon.dishes.has(herba) === false)
    )
  }

  candidates.sort((a, b) => getUnitScore(b) - getUnitScore(a))
  const target = candidates[0] ?? chef
  target.dishes.add(dish)
  return target
}

function applySmokedFiletStacks(
  pokemon: ReturnType<typeof PokemonFactory.createPokemonFromName>,
  count: number
) {
  if (count <= 0) {
    return
  }

  pokemon.addMaxHP(-5 * count)
  pokemon.addAttack(3 * count)
  pokemon.addAbilityPower(10 * count)
}
function applyDetailedPokemonOverrides(
  pokemon: ReturnType<typeof PokemonFactory.createPokemonFromName>,
  detail: Pick<
    IDetailledPokemon,
    "tm" | "supercharged" | "fireShardCount" | "smokedFiletCount"
  >
) {
  if (detail.tm && detail.tm !== Ability.DEFAULT) {
    pokemon.tm = detail.tm
    pokemon.skill = detail.tm
    pokemon.maxPP = 100
    pokemon.pp = 100
  }

  if ((detail.fireShardCount ?? 0) > 0) {
    pokemon.addAttack(3 * (detail.fireShardCount ?? 0))
    pokemon.addSpeed(3 * (detail.fireShardCount ?? 0))
  }

  applySmokedFiletStacks(pokemon, Math.min(detail.smokedFiletCount ?? 0, 10))

  if (detail.supercharged) {
    pokemon.supercharged = true
    pokemon.addSpeed(20)
    pokemon.addShield(30)
  }

  return pokemon
}

function getRareCandyEvolutionTarget(
  pokemon: ReturnType<typeof PokemonFactory.createPokemonFromName>
) {
  if (pokemon.evolution && pokemon.evolution !== Pkm.DEFAULT) {
    return pokemon.evolution
  }

  return pokemon.evolutions.find((evolution) => evolution !== Pkm.DEFAULT)
}

function createDefaultFlowerPotSetup(): SimulatorFlowerPotSetup[] {
  return FlowerPots.map((pot) => ({
    pokemon: FlowerMonByPot[pot][0],
    maxHPBonus: 0,
    apBonus: 0
  }))
}

function getUnlockedFlowerPotCount(
  floraCount: number,
  flowerPots: SimulatorFlowerPotSetup[]
) {
  let nbPots = SynergyTriggers[Synergy.FLORA].filter(
    (threshold) => threshold <= floraCount
  ).length

  if (
    floraCount >= 6 &&
    flowerPots.every((flowerPot) => {
      const pokemon = PokemonFactory.createPokemonFromName(flowerPot.pokemon)
      return pokemon.evolution === Pkm.DEFAULT
    })
  ) {
    nbPots = 5
  }

  return nbPots
}

function applyMulchToFlowerPot(
  flowerPots: SimulatorFlowerPotSetup[],
  index: number,
  item: Item
) {
  if (!Mulches.includes(item as (typeof Mulches)[number]) || !flowerPots[index]) {
    return null
  }

  const nextFlowerPots = flowerPots.map((flowerPot) => ({ ...flowerPot }))
  const target = nextFlowerPots[index]

  if (item === Item.RICH_MULCH) {
    const pokemon = PokemonFactory.createPokemonFromName(target.pokemon)
    if (pokemon.evolution === Pkm.DEFAULT) {
      return null
    }
    target.pokemon = pokemon.evolution
    return nextFlowerPots
  }

  if (item === Item.AMAZE_MULCH) {
    target.maxHPBonus += 50
    target.apBonus += 30
    return nextFlowerPots
  }

  return null
}
